import { useEffect, useRef } from "react";
import {
  useFetchOrganizationInvitations,
  useCreateOrganizationInvitation,
} from "@core/features/organization";
import {
  INVITATION_TYPE,
  useFetchInvitationTemplates,
} from "@core/features/invitation";
import { Loader } from "@themes/pollinator/ui/Loader";
import { OrgCoworkersTab } from "./OrgCoworkersTab";

type Props = {
  organizationId: number;
  programId: number;
};

/**
 * Container for OrgCoworkersTab that fetches invitations.
 * Filters to get the orgAdmin invitation for the coworkers tab.
 * Auto-creates orgAdmin invitation if none exists.
 */
export function OrgCoworkersTabContainer({ organizationId, programId }: Props) {
  const hasAttemptedCreate = useRef(false);

  const { data: invitations, isLoading } = useFetchOrganizationInvitations({
    organizationId,
  });

  const { data: templates, isLoading: isLoadingTemplates } =
    useFetchInvitationTemplates({
      programId,
      type: INVITATION_TYPE.ORG_ADMIN,
    });

  const { mutate: createInvitation } = useCreateOrganizationInvitation({
    organizationId,
  });

  // Find the coworker (orgAdmin or orgAdminPrimary) invitation
  const coworkerInvitation =
    invitations?.find(
      (inv) =>
        inv.type === INVITATION_TYPE.ORG_ADMIN ||
        inv.type === INVITATION_TYPE.ORG_ADMIN_PRIMARY
    ) ?? null;

  // Get the first orgAdmin template
  const orgAdminTemplate = templates?.[0] ?? null;

  // Auto-create orgAdmin invitation if none exists and template is available
  useEffect(() => {
    if (
      !isLoading &&
      !isLoadingTemplates &&
      invitations &&
      !coworkerInvitation &&
      orgAdminTemplate &&
      !hasAttemptedCreate.current
    ) {
      hasAttemptedCreate.current = true;
      createInvitation({
        programId,
        templateId: orgAdminTemplate.id,
        type: INVITATION_TYPE.ORG_ADMIN,
      });
    }
  }, [
    isLoading,
    isLoadingTemplates,
    invitations,
    coworkerInvitation,
    orgAdminTemplate,
    createInvitation,
    programId,
  ]);

  if (
    isLoading ||
    isLoadingTemplates ||
    (!coworkerInvitation && !hasAttemptedCreate.current)
  ) {
    return (
      <div className="flex justify-center py-12">
        <Loader />
      </div>
    );
  }

  return (
    <OrgCoworkersTab
      organizationId={organizationId}
      invitation={coworkerInvitation}
      programId={programId}
      templateId={orgAdminTemplate?.id ?? null}
    />
  );
}
